import React from 'react';
import { MessageCircle, Smartphone, Bell, Info, Eye } from 'lucide-react';

const SMS_LIMIT = 160;
const PUSH_BODY_LIMIT = 200;
const PUSH_TITLE_LIMIT = 50;

// Sample values used for the live preview only. Mirror the backend's
// `_expand_gift_tokens` behaviour 1:1 so admins see exactly what the
// customer will receive.
const PREVIEW_SAMPLE = {
  points: { nome: 'Marco', brand: 'Taco Loco', punti: '50', saldo: '320', coupon: '', codice: '' },
  coupon: { nome: 'Marco', brand: 'Taco Loco', punti: '', saldo: '', coupon: 'Sconto 10%', codice: 'SAVE10' },
};

function expandTokensPreview(text, kind) {
  if (!text) return '';
  const values = PREVIEW_SAMPLE[kind] || PREVIEW_SAMPLE.points;
  return text.replace(/\{([A-Za-z_]+)\}/g, (m, key) => {
    const v = values[key.toLowerCase()];
    return v !== undefined ? v : m; // unknown tokens kept as-is, matches backend
  });
}

/**
 * Shared notification controls used by both "Regala Punti" and "Regala
 * Coupon". Keeps the UX identical across the two flows.
 *
 * WhatsApp uses Twilio's pre-approved templates (HSM) — custom text is
 * ignored on that channel. SMS + Push send the admin's free text if
 * provided, else a sensible default.
 *
 * Props:
 *   value: { notify, channels, customTitle, customMessage }
 *   onChange: (key, val) => void
 *   kind: "points" | "coupon" — drives default placeholders
 */
export function NotificationBlock({ value, onChange, kind = 'points' }) {
  const { notify, channels, customTitle, customMessage } = value;
  const has = (ch) => channels.includes(ch);
  const toggleChannel = (ch) => {
    onChange('channels', has(ch) ? channels.filter(c => c !== ch) : [...channels, ch]);
  };

  const customAffectsAny = has('sms') || has('push');
  const onlyWhatsApp = notify && has('whatsapp') && !customAffectsAny;

  const messagePlaceholder = kind === 'points'
    ? 'Es. Ciao {nome}! Ti abbiamo regalato {punti} punti per la tua fedeltà. Saldo: {saldo}. Vieni a trovarci da {brand}!'
    : 'Es. Ciao {nome}! Ti aspetta "{coupon}" nella tua bacheca da {brand}. Codice: {codice}.';
  const titlePlaceholder = kind === 'points'
    ? 'Es. 🎁 +{punti} punti da {brand}'
    : 'Es. 🎁 Un regalo da {brand}';

  // Tokens shown as clickable chips so the admin can compose without
  // typing curly braces by hand. Inserts at the end of `field`.
  const TOKENS = kind === 'points'
    ? [
        { t: '{nome}', desc: 'nome cliente' },
        { t: '{brand}', desc: 'nome locale' },
        { t: '{punti}', desc: 'punti regalati' },
        { t: '{saldo}', desc: 'saldo aggiornato' },
      ]
    : [
        { t: '{nome}', desc: 'nome cliente' },
        { t: '{brand}', desc: 'nome locale' },
        { t: '{coupon}', desc: 'descrizione coupon' },
        { t: '{codice}', desc: 'codice coupon' },
      ];

  const insertToken = (field, token) => {
    const current = (field === 'customMessage' ? customMessage : customTitle) || '';
    const next = current + (current.endsWith(' ') || current === '' ? '' : ' ') + token;
    onChange(field, next);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-4" data-testid="gift-notification-block">
      <label className="flex items-center gap-3 cursor-pointer" data-testid="gift-notify-toggle">
        <input
          type="checkbox"
          checked={notify}
          onChange={e => onChange('notify', e.target.checked)}
          className="w-5 h-5 rounded border-slate-300 text-[#D44E20] focus:ring-[#D44E20]"
        />
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A]">Notifica il cliente</p>
          <p className="text-xs text-[#64748B]">Invia un avviso al destinatario sui canali selezionati</p>
        </div>
      </label>

      {notify && (
        <>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="gift-channels">
            {[
              { v: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
              { v: 'sms', label: 'SMS', icon: Smartphone },
              { v: 'push', label: 'Push', icon: Bell },
            ].map(ch => {
              const Icon = ch.icon;
              const on = has(ch.v);
              return (
                <button
                  key={ch.v}
                  type="button"
                  onClick={() => toggleChannel(ch.v)}
                  data-testid={`gift-channel-${ch.v}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    on
                      ? 'bg-[#D44E20] text-white border-[#D44E20]'
                      : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={15} /> {ch.label}
                </button>
              );
            })}
          </div>

          {has('whatsapp') && (
            <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="whatsapp-template-notice">
              <Info size={16} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                <strong>WhatsApp usa un template pre-approvato da Meta.</strong> Il messaggio personalizzato sotto sarà inviato solo via SMS e Push. Il template WhatsApp include automaticamente: nome cliente, brand, {kind === 'points' ? 'punti regalati e saldo aggiornato' : 'codice e descrizione del coupon'}.
              </div>
            </div>
          )}

          {customAffectsAny && (
            <div className="mt-4 space-y-3" data-testid="gift-custom-content">
              <div className="flex flex-wrap items-center gap-1.5" data-testid="gift-token-chips">
                <span className="text-[10px] text-slate-500 mr-1">Token:</span>
                {TOKENS.map(({ t, desc }) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => insertToken('customMessage', t)}
                    title={`Inserisce ${desc} nel messaggio`}
                    data-testid={`gift-token-${t.replace(/[{}]/g, '')}`}
                    className="px-2 py-0.5 rounded-md bg-[#D44E20]/10 text-[#D44E20] text-[11px] font-mono hover:bg-[#D44E20]/20 transition-colors"
                  >
                    {t}
                  </button>
                ))}
                <span className="text-[10px] text-slate-400 ml-1">vengono sostituiti automaticamente prima dell'invio</span>
              </div>

              {has('push') && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs text-[#64748B] font-medium">Titolo push (opzionale)</label>
                    <span className="text-[10px] text-slate-400 tabular-nums">{(customTitle || '').length}/{PUSH_TITLE_LIMIT}</span>
                  </div>
                  <input
                    type="text"
                    maxLength={PUSH_TITLE_LIMIT}
                    value={customTitle || ''}
                    onChange={e => onChange('customTitle', e.target.value)}
                    placeholder={titlePlaceholder}
                    data-testid="gift-custom-title"
                    className="w-full h-11 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none"
                  />
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-[#64748B] font-medium">
                    Messaggio personalizzato {has('sms') && has('push') ? '(SMS + Push)' : has('sms') ? '(SMS)' : '(Push)'}
                  </label>
                  <span className="text-[10px] text-slate-400 tabular-nums">
                    {(customMessage || '').length}/{has('sms') ? SMS_LIMIT : PUSH_BODY_LIMIT}
                  </span>
                </div>
                <textarea
                  rows={3}
                  maxLength={has('sms') ? SMS_LIMIT : PUSH_BODY_LIMIT}
                  value={customMessage || ''}
                  onChange={e => onChange('customMessage', e.target.value)}
                  placeholder={messagePlaceholder}
                  data-testid="gift-custom-message"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none resize-none"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Lasciando vuoto, il sistema invierà un messaggio di default in italiano con il link alla bacheca cliente.
                </p>
              </div>

              {/* Live preview — render with sample data so the admin sees
                  exactly how the message will look to the customer. */}
              {(customMessage || (has('push') && customTitle)) && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3" data-testid="gift-preview">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Eye size={12} className="text-slate-500" />
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Anteprima (esempio con Marco / Taco Loco)
                    </p>
                  </div>
                  {has('push') && (
                    <div className="bg-white rounded-lg border border-slate-200 p-2.5 mb-2" data-testid="preview-push">
                      <p className="text-[10px] text-slate-400 mb-1">PUSH</p>
                      {customTitle && (
                        <p className="text-sm font-semibold text-[#1A1A1A] leading-tight">
                          {expandTokensPreview(customTitle, kind)}
                        </p>
                      )}
                      <p className="text-xs text-[#64748B] leading-snug mt-0.5">
                        {customMessage
                          ? expandTokensPreview(customMessage, kind)
                          : <span className="italic text-slate-400">[messaggio default]</span>}
                      </p>
                    </div>
                  )}
                  {has('sms') && customMessage && (
                    <div className="bg-white rounded-lg border border-slate-200 p-2.5" data-testid="preview-sms">
                      <p className="text-[10px] text-slate-400 mb-1">SMS</p>
                      <p className="text-xs text-[#1A1A1A] leading-snug whitespace-pre-line">
                        {expandTokensPreview(customMessage, kind)}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1.5 tabular-nums">
                        {expandTokensPreview(customMessage, kind).length} caratteri dopo sostituzione token
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {onlyWhatsApp && (
            <p className="mt-3 text-[11px] text-slate-500" data-testid="only-wa-hint">
              Stai usando solo WhatsApp: nessun messaggio custom necessario (template Twilio pre-approvato).
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function defaultNotificationState() {
  return { notify: false, channels: [], customTitle: '', customMessage: '' };
}
